import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';

const ConciliationHistoryTooltip = ({
  conciliationLog,
}: {
  conciliationLog: any[];
}) => {
  // Función para formatear fecha
  return (
    <div style={{ padding: 10, maxHeight: '300px', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
        <Typography variant="body1" fontWeight="bold">
          Historial de Conciliaciones
        </Typography>
      </div>
      <Table size="small" style={{ border: '1px solid #e0e0e0' }}>
        <TableBody>
          <TableRow style={{ backgroundColor: '#f5f5f5' }}>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Fecha
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Tipo
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Monto Total
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Documento
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Mora
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Saldo Antes
            </TableCell>
            <TableCell style={{ fontWeight: 'bold', textAlign: 'center' }}>
              Saldo Después
            </TableCell>
          </TableRow>
          {conciliationLog.map((log: any, index: number) => (
            <TableRow
              key={index}
              style={{
                backgroundColor: index % 2 === 0 ? 'white' : '#f9f9f9',
              }}
            >
              <TableCell style={{ textAlign: 'center', fontSize: '0.85em' }}>
                {formatDate(log.date)}
              </TableCell>
              <TableCell style={{ textAlign: 'center', fontSize: '0.85em' }}>
                {log.typeEsp}
              </TableCell>
              <TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>
                {formatMoney(log.conciliationAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(log.conciliationDocumentAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(log.conciliationArrearAmount)}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(
                  log.documentAmountDebtBefore + log.arrearAmountDebtBefore,
                )}
              </TableCell>
              <TableCell style={{ textAlign: 'right' }}>
                {formatMoney(
                  log.documentAmountDebtAfter + log.arrearAmountDebtAfter,
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const DocumentAmountDetail = ({ document }: { document: any }) => {
  // Datos principales del documento
  const mainData: [string, string][] = [
    ['Anticipo', `${document.advance} %`],
    ['Monto total', formatMoney(document.amount)],
    ['Monto neto', formatMoney(document.amountNet)],
  ];

  // Datos de conciliación
  const conciliationData: [string, string][] = [
    ['Conciliado documento', formatMoney(document.documentConciliation)],
    ['Conciliado mora', formatMoney(document.arrearConciliation)],
    ['Conciliado total', formatMoney(document.amountConciliated)],
  ];

  // Datos de saldos
  const balanceData: [string, string][] = [
    ['Saldo documento', formatMoney(document.documentRemainingAmountNet)],
    ['Saldo mora', formatMoney(document.arrearAmount)],
    ['Saldo total restante', formatMoney(document.totalRemainingNet)],
  ];

  const renderSection = (
    title: string,
    data: [string, string][],
    isTotal: boolean = false,
  ) => (
    <>
      <TableRow style={{ backgroundColor: '#f5f5f5' }}>
        <TableCell
          colSpan={2}
          style={{ fontWeight: 'bold', textAlign: 'center' }}
        >
          {title}
        </TableCell>
      </TableRow>
      {data.map((row) => (
        <TableRow key={row[0]} style={{ backgroundColor: 'white' }}>
          <TableCell
            style={{ fontWeight: 'bold', paddingLeft: isTotal ? 20 : 10 }}
          >
            {row[0]}
          </TableCell>
          <TableCell align="right">{row[1]}</TableCell>
        </TableRow>
      ))}
    </>
  );

  const renderConciliationSection = () => {
    const hasConciliationLog =
      document.conciliationLog && document.conciliationLog.length > 0;

    return (
      <>
        <TableRow style={{ backgroundColor: '#f5f5f5' }}>
          <TableCell
            colSpan={2}
            style={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            Conciliaciones
            {hasConciliationLog && (
              <LightTooltip
                title={
                  <ConciliationHistoryTooltip
                    conciliationLog={document.conciliationLog}
                  />
                }
                placement="right"
              >
                <Info
                  style={{
                    marginLeft: 8,
                    fontSize: 14,
                    cursor: 'pointer',
                    color: '#3f51b5',
                  }}
                />
              </LightTooltip>
            )}
          </TableCell>
        </TableRow>
        {conciliationData.map((row) => (
          <TableRow key={row[0]} style={{ backgroundColor: 'white' }}>
            <TableCell style={{ fontWeight: 'bold', paddingLeft: 10 }}>
              {row[0]}
            </TableCell>
            <TableCell align="right">{row[1]}</TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <div style={{ padding: 10, maxHeight: '300px', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', paddingBottom: 10 }}>
        <Typography variant="body1" fontWeight="bold">
          Detalles del Documento
        </Typography>
      </div>
      <Table size="small" style={{ border: '1px solid #e0e0e0' }}>
        <TableBody>
          {renderSection('Información del Documento', mainData)}
          {renderConciliationSection()}
          {renderSection('Saldos', balanceData, true)}
        </TableBody>
      </Table>
    </div>
  );
};

export default DocumentAmountDetail;
